import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { getApiUrl } from '@/utils/api-config';
import { apiGet, apiPatch } from '@/utils/api-interceptor';
import { isRecord } from '@/lib/records';
import { MEDIA_BASE_URL } from '@/config/env';

export const SUPPORT_STATUSES = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
] as const;
export type SupportStatus = typeof SUPPORT_STATUSES[number]['value'];

export interface SupportAttachment {
  /** An absolute HTTP(S) address the admin can open. */
  url: string;
  /** What to call it on screen: the API's own name, or the file name. */
  name: string;
}

export interface SupportIssue {
  id: number;
  subject: string;
  description: string;
  status: SupportStatus;
  admin_notes: string;
  reporter_name?: string | null;
  reporter_email?: string | null;
  business_name?: string | null;
  attachment_url?: string | null;
  /** Every usable upload on the report, whichever field carried it. */
  attachments?: SupportAttachment[];
  /** Uploads the response named but that cannot be opened, so we can say so. */
  unusableAttachments?: number;
  status_changed_by?: string | null;
  status_changed_at?: string | null;
  resolved_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface IssueList {
  items: SupportIssue[];
  count: number;
  next: string | null;
  previous: string | null;
}

const KEY = ['support-issues'];
const ENDPOINT = 'admin/support-issues/';

/**
 * Reports carry their uploads under whichever of these the endpoint uses, as a
 * single value or a list, and each entry is either an address or an object
 * describing the file. Reading all of them means one report shape change does
 * not quietly hide the evidence an admin needs.
 */
const ATTACHMENT_KEYS = ['attachments', 'attachment_urls', 'attachment_url', 'attachment', 'files', 'file'];
const ADDRESS_KEYS = ['url', 'file_url', 'file', 'attachment_url', 'attachment', 'path', 'src'];
const NAME_KEYS = ['name', 'filename', 'file_name', 'title', 'original_name'];

/** Pulls the address out of an entry that is either a string or an object. */
function attachmentAddress(entry: unknown): string | null {
  if (typeof entry === 'string') return entry.trim() || null;
  if (!isRecord(entry)) return null;
  for (const key of ADDRESS_KEYS) {
    const field = entry[key];
    if (typeof field === 'string' && field.trim()) return field.trim();
  }
  return null;
}

/**
 * Uploads come back either absolute or as a media path belonging to the API
 * host, so a relative address resolves against the media host rather than the
 * admin app's own origin, where it would 404. Anything not HTTP(S) is refused.
 */
function attachmentUrl(address: string): string | null {
  try {
    const url = new URL(address, MEDIA_BASE_URL);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    // An address the browser cannot parse is reported as unusable, not linked.
    return null;
  }
}

function attachmentName(entry: unknown, url: string): string {
  if (isRecord(entry)) {
    for (const key of NAME_KEYS) {
      const field = entry[key];
      if (typeof field === 'string' && field.trim()) return field.trim();
    }
  }
  try {
    const { pathname } = new URL(url);
    const file = decodeURIComponent(pathname.slice(pathname.lastIndexOf('/') + 1));
    return file || 'Attachment';
  } catch {
    return 'Attachment';
  }
}

function parseAttachments(value: Record<string, unknown>) {
  const entries = ATTACHMENT_KEYS.flatMap((key) => {
    const field = value[key];
    if (Array.isArray(field)) return field;
    return field === null || field === undefined || field === '' ? [] : [field];
  });
  const attachments: SupportAttachment[] = [];
  const seen = new Set<string>();
  let unusableAttachments = 0;
  for (const entry of entries) {
    const address = attachmentAddress(entry);
    const url = address ? attachmentUrl(address) : null;
    if (!url) {
      unusableAttachments += 1;
      continue;
    }
    if (seen.has(url)) continue;
    seen.add(url);
    attachments.push({ url, name: attachmentName(entry, url) });
  }
  return { attachments, unusableAttachments };
}

function parseIssue(value: unknown): SupportIssue {
  if (!isRecord(value) || typeof value.id !== 'number' || !Number.isSafeInteger(value.id) || value.id <= 0 ||
    typeof value.subject !== 'string' || typeof value.description !== 'string' ||
    typeof value.admin_notes !== 'string' || typeof value.created_at !== 'string' ||
    typeof value.updated_at !== 'string' || !SUPPORT_STATUSES.some((status) => status.value === value.status)) {
    throw new Error('The support issue response contained an invalid issue.');
  }
  const status = SUPPORT_STATUSES.find((status) => status.value === value.status)!;
  const optionalText = (key: string): string | null | undefined => {
    const field = value[key];
    if (typeof field === 'string') return field;
    if (field === null || field === undefined) return null;
    throw new Error(`The support issue response contained an invalid ${key}.`);
  };
  return {
    id: value.id, subject: value.subject, description: value.description,
    admin_notes: value.admin_notes, status: status.value,
    created_at: value.created_at, updated_at: value.updated_at,
    reporter_name: optionalText('reporter_name'), reporter_email: optionalText('reporter_email'),
    business_name: optionalText('business_name'),
    // Read leniently: a list here is a shape the attachment reader handles, not a broken issue.
    attachment_url: typeof value.attachment_url === 'string' ? value.attachment_url : null,
    ...parseAttachments(value),
    status_changed_by: optionalText('status_changed_by'), status_changed_at: optionalText('status_changed_at'),
    resolved_at: optionalText('resolved_at'),
  };
}

async function readResponse(response: Response): Promise<unknown> {
  let body: unknown;
  try {
    body = await response.json();
  } catch (error) {
    console.error('Could not parse support issue response:', error);
    throw new Error(`Support issue request returned an unreadable response (${response.status}).`);
  }
  if (!response.ok) {
    const error = isRecord(body) && isRecord(body.error) ? body.error : body;
    const message = typeof error === 'string' ? error
      : isRecord(body) && typeof body.error === 'string' ? body.error
      : isRecord(error) && typeof error.message === 'string' ? error.message
      : isRecord(body) && typeof body.message === 'string' ? body.message
        : `Support issue request failed (${response.status}).`;
    const fields = isRecord(error) && isRecord(error.fields) ? error.fields : null;
    const details = fields ? Object.values(fields).flatMap((field) =>
      Array.isArray(field) ? field.filter((text): text is string => typeof text === 'string') : []
    ).join(' ') : '';
    throw new Error(details ? `${message} ${details}` : message);
  }
  if (!isRecord(body) || !('data' in body)) throw new Error('The support issue response did not contain data.');
  return body.data;
}

export function useSupportIssues({ status, search, page }: { status: string; search: string; page: number }) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...KEY, 'list', { status, search, page }],
    enabled: isAuthenticated,
    queryFn: async (): Promise<IssueList> => {
      const query = new URLSearchParams();
      if (status !== 'all') query.set('status', status);
      if (search.trim()) query.set('search', search.trim());
      if (page > 1) query.set('page', String(page));
      const suffix = query.toString();
      const body = await readResponse(await apiGet(getApiUrl(`${ENDPOINT}${suffix ? `?${suffix}` : ''}`)));
      if (Array.isArray(body)) return { items: body.map(parseIssue), count: body.length, next: null, previous: null };
      if (!isRecord(body) || !Array.isArray(body.results) || typeof body.count !== 'number' ||
        !(body.next === null || typeof body.next === 'string') ||
        !(body.previous === null || typeof body.previous === 'string')) {
        throw new Error('The support issues response did not contain a valid issue list.');
      }
      return {
        items: body.results.map(parseIssue), count: body.count,
        next: typeof body.next === 'string' ? body.next : null,
        previous: typeof body.previous === 'string' ? body.previous : null,
      };
    },
  });
}

export function useSupportIssue(id: string | undefined) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    enabled: isAuthenticated && !!id,
    queryFn: async () => {
      if (!id || !/^[1-9]\d*$/.test(id)) throw new Error('Invalid support issue ID.');
      return parseIssue(await readResponse(await apiGet(getApiUrl(`${ENDPOINT}${id}/`))));
    },
  });
}

export function useUpdateSupportIssue() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, fields }: { id: number; fields: Partial<Pick<SupportIssue, 'status' | 'admin_notes'>> }) => {
      if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Invalid support issue ID.');
      return parseIssue(await readResponse(await apiPatch(getApiUrl(`${ENDPOINT}${id}/`), fields)));
    },
    onSuccess: (issue) => {
      client.setQueryData([...KEY, 'detail', String(issue.id)], issue);
      return client.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}
